/**
 * manager-groups-context.js
 */

import React, { createContext } from 'react';
import axios from 'axios';
import { UserContext } from 'contexts/user/user-context';
import ConfirmDialog from 'components/confirm-dialog';
import sortMembers from 'components/groups/sort-members';


export const ManagerGroupsContext = createContext();


const sortManagers = (a, b) => {
  // is member
  if (Boolean(a.dateAccepted) !== Boolean(b.dateAccepted)) {
    return Boolean(a.dateAccepted) ? -1 : 1;
  } 
  // name asc
  else if (a.manager.name.toLowerCase() !== b.manager.name.toLowerCase()) {
    return a.manager.name.toLowerCase() < b.manager.name.toLowerCase() ? -1 : 1;
  }
  // type
  else {
    return a.role.name < b.role.name ? -1 : 1;
  }
}


export const ManagerGroupsProvider = (props) => {
  const {
    managers,
    children
  } = props;
  const { user } = React.useContext(UserContext);
  const [action, setAction] = React.useState();
  const [confirmAction, setConfirmAction] = React.useState();


  // PRIVATE: ON UPDATE MANAGER
  const onUpdateManager = (managerGroup, isNew = false) => {
    setPublicState(p => {
      const newManagers = isNew
        ? [...p.managers, managerGroup]
        : p.managers.map(m => m.id === managerGroup.id ? managerGroup : m);
      return { ...p, managers: newManagers.sort(sortManagers) };  
    });
  }


  // PRIVATE: ON REMOVE MANAGER
  const onRemoveManager = (managerGroup) => {
    setPublicState(p => {
      const newManagers = p.managers.filter(m => !(m.id === managerGroup.id));
      return { ...p, managers: newManagers.sort(sortManagers) };
    });
  }


  // PRIVATE: REQUEST PERFORMER
  const doRequest = async (url, config, setIsLoading) => {
    let response = null;
    setIsLoading && setIsLoading(true);
    try {
      response = await axios(url, config);
    } finally {
      setIsLoading && setIsLoading(false);
    }
    return response;
  }


  // PUBLIC: ON CREATE MANAGER REQUEST
  const onCreateRequest = async (managerEmail, role, setIsLoading) => {
    if (!managerEmail) {
      return 'Please enter an email';
    }

    try {
      const data = { role, managerEmail, userEmail: user.email, context: 'groups' };
      const response = await doRequest(`/manage/request`, { method: 'post', data, bypassErrorHandling: true }, setIsLoading);
      if (response.status === 200) {
        onUpdateManager(response.data, true);
        return true;
      }
      return response?.data || 'Unknown error';
    } catch (e) {
      return e?.response?.data?.Message || e?.response?.data || e?.message || 'Unknown error'; 
    }
  }


  // PUBLIC: ON REMOVE MANAGER REQUEST
  const onDeleteRequest = async (managerGroup, setIsLoading) => {
    setAction({
      title: 'Remove Request',
      text: <>remove the {managerGroup.role.name.toLowerCase()} request that {managerGroup.creator.id === user.id
        ? <>you sent to {managerGroup.manager.name}</>
        : <>{managerGroup.manager.name} sent to you</>}?
      </>
    });
    if (!(await confirmAction())) {
      return false;
    }

    const response = await doRequest(`/manage/request/${managerGroup.code}`, { method: 'delete' }, setIsLoading);
    if (response.status === 200) {
      onRemoveManager(managerGroup);
      return true;
    }
    return false;
  }


  // PUBLIC: ON ACCEPT MANAGER REQUEST
  const onAcceptRequest = async (managerGroup, setIsLoading) => {
    const data = { context: 'groups' };
    const response = await doRequest(`/manage/request/${managerGroup.code}`, { method: 'post', data }, setIsLoading);
    if (response.status === 200) {
      onUpdateManager(response.data);
      return true;
    }
    return false;
  }


  // PUBLIC: ON LEAVE MANAGER
  const onLeaveManager = async (managerGroup, setIsLoading) => {
    setAction({ 
      title: 'Leave Manager', 
      text: <>leave <b>{managerGroup.manager.name}'s</b> {managerGroup.role.name.toLowerCase()} group?</>
    });
    if (!(await confirmAction())) {
      return false;
    }

    const params = {role: managerGroup.role.id, managerId: managerGroup.manager.id, userId: user.id, context: 'groups' };
    const response = await doRequest(`/manage/member`, { method: 'delete', params }, setIsLoading);
    if (response.status === 200) {
      onRemoveManager(managerGroup);
      return true;
    }
    return false;
  }


  // PUBLIC INTERFACE CONTAINER
  const [publicState, setPublicState] = React.useState({
    managers: [...managers]
      .map(m => ({
        ...m,
        handlers: m.handlers?.sort(sortMembers(user))
      }))
      .sort(sortManagers),
    onCreateRequest,
    onDeleteRequest,
    onAcceptRequest,
    onLeaveManager,
  });


  // UPDATE PUBLIC STATE WHEN CONFIRM ACTION IS SET.
  // TODO: consider using a ref instead so we don't have to update the state when confirmAction is set
  React.useEffect(() => {
    setPublicState(p => ({
      ...p,
      onCreateRequest,
      onDeleteRequest,
      onAcceptRequest,
      onLeaveManager,
    }));
  // the public methods change on every render so including them would constantly be triggering this effect.
  // in reality, we only want to trigger this when confirmAction changes, recognizing that
  // each of the public functions validity is only tied to changes of the confirmAction state.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmAction]);

  
  return (<>
    <ManagerGroupsContext.Provider value={publicState}>
      {children}
    </ManagerGroupsContext.Provider>

    <ConfirmDialog
      titleStyle="warning"
      title={`Confirm ${action?.title}`}
      titleIcon='warning'
      content={<>Are you sure that you want to {action?.text}</>}
      setConfirm={setConfirmAction}
    />
  </>);
}